/**
 * Clip Snapping System
 *
 * Frame-based snapping for Canva/CapCut-style behavior.
 * Snapping happens at UI/edit time, NOT at render time.
 * Once snapped, clips have exact adjacency (gap = 0).
 */

import type { ClipTiming } from './types';

/**
 * Get the snap threshold in seconds based on project framerate.
 * Uses 2 frames as the snap zone.
 *
 * @param fps - Project framerate (e.g., 24, 30, 60)
 * @returns Snap threshold in seconds
 */
export function getSnapThreshold(fps: number): number {
  if (fps <= 0) return 0.066; // Default ~2 frames at 30fps
  const frameTime = 1 / fps;
  return frameTime * 2; // 2 frames
}

/**
 * Snap a clip's position when dragging near another clip.
 * Returns the snapped start time, or original if no snap occurred.
 *
 * @param draggedStart - Current start time of dragged clip
 * @param draggedDuration - Duration of dragged clip
 * @param otherClips - Other clips on the same track
 * @param fps - Project framerate
 * @returns Snapped start time (or original if no snap)
 */
export function snapClipPosition(
  draggedStart: number,
  draggedDuration: number,
  otherClips: Array<{ start: number; end: number; id?: string }>,
  fps: number
): { start: number; snappedTo: 'start' | 'end' | null } {
  const threshold = getSnapThreshold(fps);
  const draggedEnd = draggedStart + draggedDuration;

  for (const other of otherClips) {
    // Snap dragged clip's START to other clip's END
    // (placing dragged clip right after other)
    const gapToOtherEnd = Math.abs(draggedStart - other.end);
    if (gapToOtherEnd <= threshold) {
      return { start: other.end, snappedTo: 'end' };
    }

    // Snap dragged clip's END to other clip's START
    // (placing dragged clip right before other)
    const gapToOtherStart = Math.abs(draggedEnd - other.start);
    if (gapToOtherStart <= threshold) {
      return { start: other.start - draggedDuration, snappedTo: 'start' };
    }
  }

  return { start: draggedStart, snappedTo: null };
}

/**
 * Check if two clips are touching (gap ≈ 0).
 * Used to determine if a transition can exist between them.
 *
 * Uses 1ms tolerance to account for floating point precision
 * while still being accurate for video editing (most codecs
 * work at 24-60fps = ~17-42ms per frame).
 *
 * @param clipA - The earlier clip (outgoing)
 * @param clipB - The later clip (incoming)
 * @returns True if clips are touching
 */
export function areClipsTouching(
  clipA: { end: number },
  clipB: { start: number }
): boolean {
  // 1ms tolerance - accounts for floating point while being frame-accurate
  const EPSILON = 0.001;
  return Math.abs(clipA.end - clipB.start) < EPSILON;
}

/**
 * Validate that a transition can exist between two clips.
 * Transitions are ONLY allowed between touching clips.
 *
 * @param fromClip - The outgoing clip
 * @param toClip - The incoming clip
 * @returns True if transition is allowed
 */
export function canHaveTransition(
  fromClip: { end: number },
  toClip: { start: number }
): boolean {
  return areClipsTouching(fromClip, toClip);
}

/**
 * Find clips that are adjacent to a given clip.
 * Useful for showing transition UI options.
 *
 * @param clip - The reference clip
 * @param allClips - All clips on the same track
 * @returns Object with previous and next adjacent clips (if any)
 */
export function findAdjacentClips(
  clip: ClipTiming,
  allClips: ClipTiming[]
): { previous: ClipTiming | null; next: ClipTiming | null } {
  let previous: ClipTiming | null = null;
  let next: ClipTiming | null = null;

  for (const other of allClips) {
    if (other.id === clip.id) continue;

    // Check if other clip ends where this one starts
    if (areClipsTouching(other, clip)) {
      previous = other;
    }

    // Check if this clip ends where other starts
    if (areClipsTouching(clip, other)) {
      next = other;
    }
  }

  return { previous, next };
}

/**
 * Round a time value to the nearest frame boundary.
 * Useful for ensuring clean frame alignment.
 *
 * @param time - Time in seconds
 * @param fps - Project framerate
 * @returns Time rounded to nearest frame
 */
export function roundToFrame(time: number, fps: number): number {
  if (fps <= 0) return time;
  const frameTime = 1 / fps;
  return Math.round(time / frameTime) * frameTime;
}
